import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../hooks/useAnalytics';
import analyticsService from '../services/analytics';

export const AnalyticsTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view with existing analytics
    const pageName = location.pathname === '/' ? 'Home' : location.pathname.slice(1);
    trackPageView(pageName);
    
    // Track with enhanced analytics service (include hash for better tracking)
    const enhancedPageName = getPageName(location.pathname, location.hash);
    const fullLocation = location.pathname + location.hash;
    analyticsService.trackPageView(enhancedPageName, fullLocation);
    
    // Track funnel steps based on page
    trackFunnelStep(location.pathname);
  }, [location]);

  return null; // This component doesn't render anything
};

// Helper function to get human-readable page names
function getPageName(pathname: string, hash?: string): string {
  const routes: { [key: string]: string } = {
    // Landing
    '/': 'Home - Landing Page',

    // Core Tools (Value Proposition)
    '/research': 'Stock Research',
    '/stock-research': 'Stock Research', // legacy path — 301 redirects to /research
    '/chat': 'AI Chat',
    '/portfolio/risk-assessment': 'Portfolio Risk Assessment',
    '/portfolio/risk-results': 'Portfolio Risk Results',

    // Legacy Agent Routes
    '/agent/jerry': 'Stock Research Dashboard',
    '/agent/xavier': 'Sector Intelligence Dashboard',
    '/agent/bradford': 'Portfolio Risk Dashboard',
    '/agent/charles': 'Volume Analysis Dashboard',
    '/agent/apollo': 'Market Intelligence Dashboard',
    '/agent/kelly': 'Dividend Quality Dashboard',
    '/agent/nikki': 'News Intelligence Dashboard',

    // User Data Pages
    '/portfolios': 'My Portfolios',
    '/watchlist': 'My Watchlist',
    '/profile': 'User Profile',
    '/billing': 'Billing',

    // Info Pages
    '/academy': 'Academy',
    '/learn': 'Learning Center',
    '/faq': 'FAQ',
    '/about': 'About',
    '/security': 'Security',
    '/privacy': 'Privacy Policy',
    '/terms': 'Terms and Conditions',

    // Legacy
    '/product': 'Product Page',
    '/archive': 'Archive Search',
  };
  
  // Handle hash-specific page names
  if (hash) {
    const cleanHash = hash.replace('#', '');
    if (pathname === '/product' && cleanHash === 'choose-your-plan') {
      return 'Product Page - Choose Your Plan';
    }
    if (pathname === '/academy') {
      const academyMap: { [key: string]: string } = {
        'stock-fundamentals': 'Academy - Stock Fundamentals',
        'cryptocurrency-signal-intelligence': 'Academy - Cryptocurrency Signal Intelligence',
        'apollo-whale-tracking': 'Academy - Apollo Whale Tracking',
        'apollo-signal-detection': 'Academy - Apollo Signal Detection',
        'apollo-risk-management': 'Academy - Apollo Risk Management',
        'adjacent-sector-correlations': 'Academy - Adjacent Sector Correlations',
        'technical-indicators': 'Academy - Technical Indicators',
        'market-data': 'Academy - Market Data',
        'ai-indicators': 'Academy - AI Indicators',
        'risk-management': 'Academy - Risk Management',
        'time-horizons': 'Academy - Time Horizons',
        'valuation': 'Academy - Valuation Metrics',
        'msdi-score': 'Academy - MSDI Score',
      };
      if (academyMap[cleanHash]) {
        return academyMap[cleanHash];
      }
    }
  }
  
  // Check for dynamic routes
  if (pathname.startsWith('/agent/xavier/')) {
    return 'Sector Opportunity Detail';
  }
  if (pathname.startsWith('/details/')) {
    return 'Asset Details';
  }
  if (pathname.startsWith('/watchlist/details/')) {
    return 'Watchlist Asset Details';
  }
  if (pathname.startsWith('/archive/')) {
    return 'Archive Report View';
  }
  
  return routes[pathname] || 'Unknown Page';
}

// Track funnel steps based on page visits
function trackFunnelStep(pathname: string) {
  // Track return visits
  analyticsService.trackReturnVisit();

  // Track landing on home page (first visit)
  if (pathname === '/') {
    const hasVisitedBefore = localStorage.getItem('has_visited');
    if (!hasVisitedBefore) {
      analyticsService.trackFunnelStep('landing');
      localStorage.setItem('has_visited', 'true');
    }
  }

  // Track tool discovery (core value proposition pages)
  if (pathname === '/research' || pathname === '/stock-research') {
    analyticsService.trackToolDiscovered('stock_research');
  } else if (pathname === '/chat') {
    analyticsService.trackToolDiscovered('chat');
  } else if (pathname === '/portfolio/risk-assessment' || pathname === '/portfolio/risk-results') {
    analyticsService.trackToolDiscovered('portfolio_analysis');
  }

  // Track engagement when visiting key pages
  const engagementPages = [
    '/research', '/stock-research', '/chat', '/portfolio/risk-assessment', '/portfolio/risk-results',
    '/academy', '/learn', '/faq', '/product',
    '/agent/jerry', '/agent/xavier', '/agent/apollo', '/agent/kelly', '/agent/charles', '/agent/bradford', '/agent/nikki'
  ];
  if (engagementPages.includes(pathname)) {
    const hasEngaged = localStorage.getItem('has_engaged');
    if (!hasEngaged) {
      analyticsService.trackFunnelStep('engaged', {
        first_engagement_page: pathname,
      });
      localStorage.setItem('has_engaged', 'true');
    }
  }
}